<script>
import BaseModal from '@/components/_base-modal.vue'
import { mapActions } from 'vuex'
import { Excel, Csv, FILE_EXTENSIONS } from '@/shared/constants/core/FileTypes'
import baseMixin from '@/mixins/baseMixin'
import dateMixin from '@/mixins/dateMixin'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { getTodaysDateString } from '@/helpers/date-helpers'
import { downloadBlob } from '@/helpers/file-helpers'

export default {
  name: 'CandidateExportDialog',
  components: {
    BaseModal,
  },
  mixins: [baseMixin, dateMixin],
  props: {
    locationIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    show: false,
    isLoading: false,
    startDate: null,
    endDate: null,
    showStartPicker: false,
    showEndPicker: false,
    exportFormat: Excel,
  }),
  computed: {
    formattedStartDate() {
      return this.startDate
        ? this.formatDate(this.startDate, DateFormatToken.DoMMMYYYY)
        : ''
    },
    formattedEndDate() {
      return this.endDate
        ? this.formatDate(this.endDate, DateFormatToken.DoMMMYYYY)
        : ''
    },
    isExportDisabled() {
      return this.isLoading || !this.startDate || !this.endDate
    },
    maxStartDate() {
      return this.endDate || getTodaysDateString()
    },
    maxEndDate() {
      return getTodaysDateString()
    },
    fileExtension() {
      return FILE_EXTENSIONS[this.exportFormat] ?? this.exportFormat
    },
    fileName() {
      return `EducatorExport(${this.formattedStartDate} - ${this.formattedEndDate}).${this.fileExtension}`
    },
    exportFormatOptions() {
      return [
        {
          value: Excel,
          label: this.$t('candidateExport.formatExcel'),
          icon: 'mdi-file-excel',
        },
        {
          value: Csv,
          label: this.$t('candidateExport.formatCsv'),
          icon: 'mdi-file-delimited',
        },
      ]
    },
    selectedFormatColor() {
      const option = this.exportFormatOptions.find(
        (o) => o.value === this.exportFormat
      )
      return option?.color ?? 'secondary'
    },
  },
  methods: {
    ...mapActions({
      exportCandidates: 'candidate/exportCandidates',
    }),
    open() {
      this.show = true
    },
    close() {
      this.show = false
    },
    async handleExport() {
      this.isLoading = true

      const exportData = await this.exportCandidates({
        locationIds: this.locationIds,
        startDate: this.startDate,
        endDate: this.endDate,
        format: this.exportFormat,
        useACECQATemplate: false,
      })

      if (!exportData) {
        this.isLoading = false
        return
      }

      downloadBlob(exportData, this.fileName)

      this.isLoading = false
      this.show = false
    },
  },
}
</script>

<template>
  <div>
    <v-btn
      outlined
      class="export-button"
      :loading="isLoading"
      :disabled="isLoading"
      height="40"
      @click="open"
    >
      <v-icon size="20" color="grey darken-1">mdi-tray-arrow-down</v-icon>
    </v-btn>

    <BaseModal
      v-model="show"
      :title="$t('candidateExport.title')"
      hide-header-divider
      hide-padding
      width="600px"
    >
      <div class="px-6 mb-6">
        <div>
          <p class="text-body-1 mb-3">
            {{ $t('candidateExport.description') }}
          </p>
          <div class="d-flex align-center">
            <v-btn-toggle
              v-model="exportFormat"
              mandatory
              borderless
              dense
              :color="selectedFormatColor"
            >
              <v-btn
                v-for="option in exportFormatOptions"
                :key="option.value"
                :value="option.value"
                class="px-4 font-weight-medium text-body-1"
                :class="
                  exportFormat === option.value
                    ? `secondary--text`
                    : 'grey--text'
                "
              >
                <v-icon
                  left
                  :color="exportFormat === option.value ? 'secondary' : 'grey'"
                  >{{ option.icon }}</v-icon
                >
                {{ option.label }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <div class="my-6">
          <p class="text-body-1 mb-3">
            {{ $t('candidateExport.dateRangeLabel') }}
          </p>
          <v-row>
            <v-col cols="4">
              <v-menu
                v-model="showStartPicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-text-field
                      :value="formattedStartDate"
                      :label="$t('candidateExport.startDateLabel')"
                      readonly
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      class="pointer-events-none"
                    />
                  </div>
                </template>
                <v-date-picker
                  v-model="startDate"
                  no-title
                  :max="maxStartDate"
                  @input="showStartPicker = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="4">
              <v-menu
                v-model="showEndPicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-text-field
                      :value="formattedEndDate"
                      :label="$t('candidateExport.endDateLabel')"
                      readonly
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      class="pointer-events-none"
                    />
                  </div>
                </template>
                <v-date-picker
                  v-model="endDate"
                  no-title
                  :min="startDate"
                  :max="maxEndDate"
                  @input="showEndPicker = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </div>
      </div>

      <template v-slot:actions>
        <BaseButton
          sm
          color="primary"
          :loading="isLoading"
          :disabled="isExportDisabled"
          @click="handleExport"
        >
          {{ $t('candidateExport.exportButton') }}
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.export-button {
  border-color: $color-neutral-grey-200;
  border-radius: 8px;

  &:hover {
    color: $color-blue-500 !important;
    background-color: $color-info-270 !important;
    border-color: $color-info-300 !important;

    ::v-deep .v-icon {
      color: $color-blue-400 !important;
    }
  }
}

::v-deep .v-card__title .text-h6 {
  font-size: 1.5rem !important;
}

.date-picker-activator {
  width: 190px;
}
</style>
